import {
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import { currentDatabaseSelector, selectedDatabaseIdState } from '@transport-insights/uikit';
import {
  useAverageLifeAchievedAsphalt,
  useAverageLifeAchievedChipseal,
  useAverageLifeAchievedPavement,
} from '@src/modules/performance/context/reports-api-hooks';

import Chart from 'react-apexcharts';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { useRecoilValue } from 'recoil';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Average Life Achieved',
  content: [
    {
      title: null,
      description: (
        <>
          <Text fontSize="inherit" mb={2} fontWeight="bold">Important limitation: These charts are an indicative view only and should not be used as a definitive result of average life achieved.</Text>
          <Text fontSize="inherit" mb={2}>Results are calculated only from the surface records that apply the work origin coding for NZTA work categories 212 and 214. Determining reliable results depends on the completeness and accuracy of surfacing dates, surface history, and work category attribution for surface records.</Text>
          <Text fontSize="inherit" mb={2}>In particular, the current calculation uses work categories to distinguish pavement rehabilitation from resurfacing. This may not always reflect the reason for installing a replacement surface.</Text>
          <Text fontSize="inherit" mb={2}>The calculation and presentation are under review.</Text>
          <Text fontSize="inherit">Users should validate results against local asset records and knowledge before using them in AMPs, investment decisions, or performance reporting.</Text>
        </>
      ),
      source: null,
    },
    {
      title: 'Average Life Achieved (Pavement Rehab)',
      description: (
        <>
          <Text fontSize="inherit" mb={2}>
            The average life achieved for sealed road surfaces that have had pavement rehabilitation, for the network compared to the average life achieved for the peer group and for National.
          </Text>
          <Text fontSize="inherit">
            The Average Life is calculated as the average of the duration between the pavement being laid and being replaced, for pavement layers with a Work Category of 214 that have been replaced in the last four years.
          </Text>
        </>
      ),
      source: null,
    },
    {
      title: 'Average Life Achieved (Asphalt)',
      description: (
        <>
          <Text fontSize="inherit" mb={2}>
            The average life achieved for asphalt surfaces that have been resurfaced, for the network compared to the average life achieved for the peer group and for National.
          </Text>
          <Text fontSize="inherit">
            The Average Life is calculated as the average of the duration between the asphalt surface being laid and being overlaid, for surfaces with a Work Category of 212 that have been replaced in the last four years.
          </Text>
        </>
      ),
      source: null,
    },
    {
      title: 'Average Life Achieved (Chipseal)',
      description: (
        <>
          <Text fontSize="inherit" mb={2}>
            The average life achieved for chipsealed surfaces that have been resurfaced, for the network compared to the average life achieved for the peer group and for National.
          </Text>
          <Text fontSize="inherit">
            The Average Life is calculated as the average of the duration between the chipseal surface being laid and being overlaid, for surfaces with a Work Category of 212 that have been replaced in the last four years.
          </Text>
        </>
      ),
      source: (
        <Text fontSize="inherit" as="span">
          Calculated by REG using surface and pavement layer data from AWM.
          Information on Work Categories 212 and 214 is available from the NZTA web site and the
          {' '}
          <Link
            href="https://nzta.govt.nz/assets/Road-Efficiency-Group/docs/practice-overviews/REG-practice-overview-work-origin.pdf"
            isExternal
          >
            REG Practice Overview on Work Origin.
          </Link>
        </Text>
      ),
    },
  ],
};

// Helper function to build chart data for a given dataset
const buildChartData = (years, data, currentDatabase, isRegionNationalOrStateHighways, isNational) => {
  const chartData = [
    {
      name: currentDatabase?.name || 'TA',
      type: 'line',
      data: years.map((year) => {
        const item = data?.find((x) => x.financialYear === year && x.series === 'Council');
        return {
          x: year,
          y: item ? Math.round(item.value * 10) / 10 : null,
        };
      }),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Peer Group',
      type: 'line',
      data: years.map((year) => {
        const item = data?.find((x) => x.financialYear === year && x.series === 'PeerGroup');
        return {
          x: year,
          y: item ? Math.round(item.value * 10) / 10 : null,
        };
      }),
      color: CHART_COLOURS.black,
    },
    ...(!isNational
      ? [
        {
          name: 'National',
          type: 'line',
          data: years.map((year) => {
            const item = data?.find((x) => x.financialYear === year && x.series === 'National');
            return {
              x: year,
              y: item ? Math.round(item.value * 10) / 10 : null,
            };
          }),
          color: CHART_COLOURS.lightgrey,
        },
      ]
      : []),
  ];

  const chartSeries = isRegionNationalOrStateHighways
    ? [...chartData.filter((x) => !x.name?.includes('Peer'))]
    : [...chartData];
  return chartSeries;
};

// Component for rendering a line chart with given title, years, and chart series
function LineChart({ title, years, chartSeries }) {
  return (
    <Chart
      options={
          {
            title: {
              text: title,
            },
            chart: {
              animations: {
                enabled: false,
              },
            },
            xaxis: {
              categories: years,
              labels: {
                offsetY: 5,
                rotate: -45,
                rotateAlways: true,
              },
            },
            yaxis: [
              {
                forceNiceScale: false,
                title: {
                  text: 'Average Life (Years)',
                },
                labels: {
                  formatter(y) {
                    return y !== null ? `${y.toFixed(1)}` : '';
                  },
                },
              },
            ],
            grid: {
              padding: {
                left: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            tooltip: {
              shared: true,
              intersect: false,
              y: {
                formatter(val) {
                  return val !== null ? `${val.toFixed(1)}` : '-';
                },
              },
            },
          }
        }
      series={chartSeries}
      type="line"
      width="100%"
      height="280"
    />
  );
}

function NetworkPhysicalCharacteristicsAverageLife() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: chipsealData, isLoading: isLoadingChipseal } = useAverageLifeAchievedChipseal();
  const { data: asphaltData, isLoading: isLoadingAsphalt } = useAverageLifeAchievedAsphalt();
  const { data: pavementData, isLoading: isLoadingPavement } = useAverageLifeAchievedPavement();
  const isRegionNationalOrStateHighways = rcaId === 81 || rcaId >= 1000;
  const isNational = rcaId === 1000;

  const isLoading = isLoadingChipseal || isLoadingAsphalt || isLoadingPavement;
  const lockYear = chipsealData?.[0]?.lockYear || asphaltData?.[0]?.lockYear || pavementData?.[0]?.lockYear || null;

  // Chipseal Chart
  const chipsealYears = [...new Set(chipsealData?.map((x) => x.financialYear))].sort();
  const chipsealChartSeries = buildChartData(chipsealYears, chipsealData, currentDatabase, isRegionNationalOrStateHighways, isNational) || [];

  // Asphalt Chart
  const asphaltYears = [...new Set(asphaltData?.map((x) => x.financialYear))].sort();
  const asphaltChartSeries = buildChartData(asphaltYears, asphaltData, currentDatabase, isRegionNationalOrStateHighways, isNational) || [];

  // Pavement Rehab Chart
  const pavementYears = [...new Set(pavementData?.map((x) => x.financialYear))].sort();
  const pavementChartSeries = buildChartData(pavementYears, pavementData, currentDatabase, isRegionNationalOrStateHighways, isNational) || [];

  return (
    <ReportSection
      title="Average Life Achieved — use with caution (under review)"
      subtitle="Dependent on the completeness and accuracy of work-origin and surface-history data"
      isLoading={isLoading || !chipsealData || !asphaltData || !pavementData}
      informationSource={informationSource}
      lockYear={lockYear}
    >
      <Wrap spacing={0} justify="center">
        <LineChart
          title="Average Life Achieved (Pavement Rehab)"
          years={pavementYears}
          chartSeries={pavementChartSeries}
        />
        <LineChart
          title="Average Life Achieved (Asphalt)"
          years={asphaltYears}
          chartSeries={asphaltChartSeries}
        />
        <LineChart
          title="Average Life Achieved (Chipseal)"
          years={chipsealYears}
          chartSeries={chipsealChartSeries}
        />
      </Wrap>
    </ReportSection>
  );
}

export default NetworkPhysicalCharacteristicsAverageLife;
